import { memo, useState } from "react";
import { Plus, Minus, Check, Heart, Info } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { ProductDetailModal } from "./ProductDetailModal";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoritesContext";
import { formatCOP } from "../lib/api";
import { formatUnit } from "../lib/units";
import { toast } from "sonner";

// Color palette indexed by category hash for consistent per-category color
const PLACEHOLDER_PALETTE = [
  { bg: "#FEF3C7", fg: "#92400E" }, // amber
  { bg: "#DBEAFE", fg: "#1E40AF" }, // blue
  { bg: "#FCE7F3", fg: "#9F1239" }, // pink
  { bg: "#D1FAE5", fg: "#065F46" }, // green
  { bg: "#E0E7FF", fg: "#3730A3" }, // indigo
  { bg: "#FED7AA", fg: "#9A3412" }, // orange
  { bg: "#F3E8FF", fg: "#6B21A8" }, // purple
  { bg: "#FEE2E2", fg: "#991B1B" }, // red
  { bg: "#CCFBF1", fg: "#115E59" }, // teal
];

const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const ProductPlaceholder = ({ categoria, codigo }) => {
  const p = PLACEHOLDER_PALETTE[hashStr(categoria || "") % PLACEHOLDER_PALETTE.length];
  const initials = (categoria || "·").trim().slice(0, 2).toUpperCase();
  return (
    <div
      className="h-full w-full flex flex-col items-center justify-center relative"
      style={{ background: `linear-gradient(135deg, ${p.bg} 0%, ${p.bg}88 100%)` }}
    >
      <span className="font-serif text-5xl font-medium tracking-tight" style={{ color: p.fg }}>
        {initials}
      </span>
      <span className="text-[10px] mt-1 font-mono uppercase tracking-widest opacity-50 px-2 text-center line-clamp-2" style={{ color: p.fg }}>
        {categoria || "Producto"}
      </span>
    </div>
  );
};

export const ProductCard = memo(function ProductCard({ product, onFavoriteToggle }) {
  const { add } = useCart();
  const { isFavorite, toggle: toggleFavorite } = useFavorites();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const fav = isFavorite(product.codigo);

  const onAdd = () => {
    if (!product.disponible) {
      toast.info("Este producto requiere consultar precio con CM");
      return;
    }
    add(product, qty);
    setAdded(true);
    toast.success(`${product.descripcion.substring(0, 40)}... agregado`, { duration: 1500 });
    setTimeout(() => setAdded(false), 1200);
  };

  const onToggleFav = async (e) => {
    e.stopPropagation();
    try {
      await toggleFavorite(product.codigo);
      toast.success(fav ? "Eliminado de favoritos" : "Agregado a favoritos", { duration: 1200 });
      onFavoriteToggle?.();
    } catch {
      toast.error("No se pudo actualizar favorito");
    }
  };

  const lowStock = typeof product.inventario === "number" && product.inventario > 0 && product.inventario < 50;
  const outOfStock = typeof product.inventario === "number" && product.inventario <= 0;

  return (
    <>
    <article
      className="group bg-white border border-cm-border rounded-md overflow-hidden flex flex-col hover:border-cm-ink/30 transition-all"
      data-testid={`product-card-${product.codigo}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => setDetailOpen(true)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setDetailOpen(true)}
        aria-label="Ver ficha completa del producto"
        className="relative aspect-square bg-cm-cream-alt overflow-hidden text-left cursor-zoom-in"
        data-testid={`product-image-${product.codigo}`}
      >
        {product.imagen ? (
          <img
            src={product.imagen}
            alt={product.descripcion}
            loading="lazy"
            decoding="async"
            width={320}
            height={320}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <ProductPlaceholder categoria={product.sublinea || product.linea || product.categoria} codigo={product.codigo} />
        )}
        <div className="absolute inset-0 bg-cm-ink/0 group-hover:bg-cm-ink/10 transition-colors flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100 pointer-events-none">
          <span className="text-[10px] bg-cm-ink text-cm-cream px-2 py-1 rounded uppercase tracking-widest flex items-center gap-1">
            <Info className="h-3 w-3" /> Ver ficha
          </span>
        </div>
        <div className="absolute top-2 left-2 flex gap-1.5 pointer-events-none">
          <Badge className="bg-cm-ink text-cm-cream text-[10px] hover:bg-cm-ink font-mono">{product.codigo}</Badge>
        </div>
        <button
          type="button"
          onClick={onToggleFav}
          aria-label={fav ? "Quitar de favoritos" : "Guardar en favoritos"}
          title={fav ? "Quitar de favoritos" : "Guardar en favoritos"}
          className={`absolute top-2 right-2 h-7 w-7 rounded-full backdrop-blur-sm flex items-center justify-center transition-all ${
            fav
              ? "bg-cm-red text-white hover:bg-cm-red/90"
              : "bg-white/80 text-cm-ink-soft hover:bg-white hover:text-cm-red"
          } ${lowStock || outOfStock ? "top-9" : ""}`}
          data-testid={`favorite-toggle-${product.codigo}`}
        >
          <Heart className={`h-3.5 w-3.5 ${fav ? "fill-current" : ""}`} />
        </button>
        {lowStock && !outOfStock && (
          <Badge className="absolute top-2 right-2 bg-cm-yellow text-cm-ink hover:bg-cm-yellow text-[10px] pointer-events-none">
            Quedan {product.inventario}
          </Badge>
        )}
        {outOfStock && (
          <Badge className="absolute top-2 right-2 bg-cm-red text-white hover:bg-cm-red text-[10px] pointer-events-none">
            Agotado
          </Badge>
        )}
      </div>

      <div className="p-3 flex-1 flex flex-col">
        <p className="text-[10px] uppercase tracking-wider text-cm-ink-soft font-medium line-clamp-1">
          {product.sublinea || product.linea || product.categoria}
          {product.marca ? ` · ${product.marca}` : ""}
        </p>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => setDetailOpen(true)}
              className="text-sm font-medium leading-snug text-cm-ink line-clamp-2 mt-0.5 min-h-[2.5rem] text-left hover:text-cm-red transition-colors"
              data-testid={`product-name-${product.codigo}`}
            >
              {product.descripcion}
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs bg-cm-ink text-cm-cream border-0 text-xs">
            <p className="font-medium leading-snug">{product.descripcion}</p>
            <p className="mt-1 text-cm-cream/60 font-mono">#{product.codigo} · {formatUnit(product.unidad)}</p>
            <p className="mt-1 text-cm-yellow/80 text-[10px] uppercase tracking-widest">Click para ver ficha</p>
          </TooltipContent>
        </Tooltip>
        <p className="text-xs text-cm-ink-soft mt-1">
          {formatUnit(product.unidad)}
        </p>

        <div className="mt-2 flex items-baseline justify-between">
          {product.disponible ? (
            <div className="flex items-baseline gap-2">
              <p className="text-lg font-bold font-mono text-cm-ink" data-testid={`product-price-${product.codigo}`}>
                {formatCOP(product.precio)}
              </p>
              {product.tiene_psm && (
                <span className="px-1.5 py-0.5 bg-cm-yellow text-cm-ink text-[9px] font-bold uppercase tracking-widest rounded" title="Precio especial negociado para tu cliente">
                  PSM
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm font-medium text-cm-red italic">Consultar precio</p>
          )}
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          <div className="flex items-center border border-cm-border rounded">
            <button
              onClick={() => setQty(Math.max(1, qty - 1))}
              className="h-8 w-7 hover:bg-cm-cream-alt flex items-center justify-center"
              data-testid={`product-qty-decrease-${product.codigo}`}
              disabled={!product.disponible}
            ><Minus className="h-3 w-3" /></button>
            <input
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-10 h-8 text-center text-sm font-mono bg-transparent focus:outline-none"
              data-testid={`product-qty-input-${product.codigo}`}
              disabled={!product.disponible}
            />
            <button
              onClick={() => setQty(qty + 1)}
              className="h-8 w-7 hover:bg-cm-cream-alt flex items-center justify-center"
              data-testid={`product-qty-increase-${product.codigo}`}
              disabled={!product.disponible}
            ><Plus className="h-3 w-3" /></button>
          </div>
          <Button
            onClick={onAdd}
            size="sm"
            className={`flex-1 h-8 transition-all ${added ? "bg-green-600 hover:bg-green-600" : "btn-cm-primary"}`}
            disabled={!product.disponible || outOfStock}
            data-testid={`add-to-cart-${product.codigo}`}
          >
            {added ? <Check className="h-4 w-4" /> : "Agregar"}
          </Button>
        </div>
      </div>
    </article>
    {detailOpen && (
      <ProductDetailModal
        codigo={product.codigo}
        initial={product}
        open
        onClose={() => setDetailOpen(false)}
      />
    )}
    </>
  );
});
